import { computed, type ComputedRef } from 'vue';
import { useMainStore } from '../stores/mainStore.js';

interface StyleTag {
  innerHTML: string;
  type: string;
}

interface HeaderCriticalCssResult {
  /** Whether the new mobile header is active for this site. */
  newMobileHeaderActive: ComputedRef<boolean>;
  styleTags: StyleTag[];
}

/**
 * Generates all critical CSS needed to prevent header FOUC:
 *
 * 1. When the new mobile header is active, hides the old desktop header/nav at
 *    mobile widths so it doesn't flash before the component mounts.
 *
 * 2. Reserves vertical space for `#view-header` so page content does not reflow
 *    downward once the header renders and measures itself.
 *
 * Must be called in setup() so styles are injected server-side before first paint.
 */
export function useHeaderCriticalCss(): HeaderCriticalCssResult {
  const mainStore = useMainStore();
  const headerVersion: string = mainStore.theme?.header?.options?.version ?? '';
  const menuVersion: string = mainStore.theme?.menu?.options?.version ?? '';
  const isBarNav = menuVersion === 'M4' || menuVersion === 'M5';

  const newMobileHeaderActive = computed(
    () => mainStore.theme?.header?.options?.version === 'H3' || !!mainStore.featureFlags?.cms_new_mobile_header_enabled,
  );

  const styleTags: StyleTag[] = [];
  const mobileBreakpoint = isBarNav ? 1023 : 960;

  // --- 1. Hide old desktop header/nav on mobile when new mobile header is active ---
  if (newMobileHeaderActive.value) {
    const headerBreakpoint = mobileBreakpoint;
    styleTags.push({
      innerHTML: `@media (max-width:${headerBreakpoint}px){#h1 .header-container,#h2 .header-container,#h2 [data-testid="small-utilities-bar"] .search,#h3 .utility-menu,.h7>.header-container,.h8>.header-container{display:none!important}}@media (max-width:600px){.h5>.header-container,.h5>.bottom-bar{display:none!important}}@media (max-width:1023px){#m4,#m5{display:none!important}}`,
      type: 'text/css',
    });
  }

  // --- 2. Reserve vertical space for the header to prevent content reflow ---
  // Only needed when the new mobile header is active — headers with their own
  // native mobile layout (e.g. H4) don't render MobileHeader and don't need it.
  if (newMobileHeaderActive.value) {
    // Desktop min-heights per header version. Bar nav (M4/M5) adds ~54px.
    const desktopHeights: Record<string, number> = {
      H1: 74,
      H2: 80,
      H3: 80,
      H5: 112,
      H6: 80,
      H7: 80,
    };
    const desktopHeight = (desktopHeights[headerVersion] ?? 80) + (isBarNav ? 54 : 0);

    // Mobile heights per header version.
    // H3: padding-top 16px + logo/content ~48px = ~64px.
    // H4: top-header ~48px + bottom-header (padding 24px×2 + logo 64px) = ~160px.
    //     H4 has its own mobile layout (no MobileHeader), so it's shorter than the default.
    // H7/H8: override MobileHeader's slot; no TopBar on mobile; mobile-header-top
    //        padding (24+24=48px) + logo (48px) = ~96px. No utilities bar.
    // All others: MobileHeader (48px padding-top + 115px main-content = 163px)
    //             + utilities-top bar (~55px when links present) = ~218px.
    const hasUtilities = (mainStore.homepage?.header?.links?.length ?? 0) > 0;
    const mobileHeights: Record<string, number> = {
      H3: 64,
      H4: 160,
      H7: 128,
      H8: 128,
    };
    const mobileHeight = mobileHeights[headerVersion] ?? (hasUtilities ? 218 : 163);

    // Mirrors CmsHeader.vue's isMobile logic:
    // bar-nav (M4/M5) stays mobile until 1023px; all others switch at 960px.
    const desktopBreakpoint = isBarNav ? 1024 : 961;

    if (headerVersion !== 'H4') {
      // H4 is excluded: it uses position:absolute on desktop (overlays the hero gallery,
      // no space reservation needed) and intentionally transitions from gallery-behind-header
      // to solid on mobile — injecting any CSS here would break that designed behavior.
      styleTags.push({
        innerHTML: `#view-header{min-height:${mobileHeight}px}@media(min-width:${desktopBreakpoint}px){#view-header{min-height:${desktopHeight}px}}`,
        type: 'text/css',
      });
    }
  }

  // --- 3. Pre-center H2 small utilities bar on mobile to prevent hydration flash ---
  // screenSize starts as 0 (isMobile=false) when initialIsMobile is not set, so the
  // utility bar renders right-aligned on first paint. On client mount setScreenSize()
  // fires, isMobile becomes true, and the is-mobile class shifts links to center-aligned.
  // Pre-applying center alignment in critical CSS eliminates that visible shift.
  if (headerVersion === 'H2') {
    styleTags.push({
      innerHTML: `@media (max-width:${mobileBreakpoint}px){#h2 [data-testid="small-utilities-bar"] .utility-bar-items{justify-content:center}}`,
      type: 'text/css',
    });
  }

  return { newMobileHeaderActive, styleTags };
}
